import { BEAD_SPECS } from './beads';
import { colorCounts, symbolForIndex } from './pattern';
import { STITCH_SPECS, peyoteCount, physicalSize, wovenRows } from './stitches';
import { EMPTY } from './types';
import type { BeadColor, Pattern } from './types';

export interface BeadCountRow {
  index: number;
  color: BeadColor;
  symbol: string;
  count: number;
  /** Beads needed including a waste margin. */
  withSpares: number;
  grams: number;
  percent: number;
}

const SPARE_FACTOR = 1.12;

export function beadCounts(pattern: Pattern): BeadCountRow[] {
  const counts = colorCounts(pattern);
  const total = [...counts.values()].reduce((a, b) => a + b, 0) || 1;
  const perGram = BEAD_SPECS[pattern.beadSize].perGram;
  return [...counts.entries()]
    .map(([index, count]) => {
      const withSpares = Math.ceil(count * SPARE_FACTOR);
      return {
        index,
        color: pattern.palette[index],
        symbol: symbolForIndex(index),
        count,
        withSpares,
        grams: withSpares / perGram,
        percent: (count / total) * 100,
      };
    })
    .sort((a, b) => b.count - a.count);
}

export function totalBeads(pattern: Pattern): number {
  let n = 0;
  for (let i = 0; i < pattern.cells.length; i++) if (pattern.cells[i] !== EMPTY) n += 1;
  return n;
}

/** Rough thread length needed, in metres — 2 passes through every bead. */
export function threadEstimate(pattern: Pattern): number {
  const spec = BEAD_SPECS[pattern.beadSize];
  const beads = totalBeads(pattern);
  return (beads * spec.len * 2.6) / 1000;
}

export interface WordChartOptions {
  /** Use manufacturer codes rather than colour names. */
  useCodes: boolean;
  /** Collapse runs of the same colour into "3x". */
  runLength: boolean;
}

function labelOf(color: BeadColor | undefined, useCodes: boolean): string {
  if (!color) return '?';
  if (useCodes && color.code) return color.code;
  return color.name || color.code || color.hex;
}

/**
 * Row-by-row stitching instructions.
 *
 * Rows come out in working order (bottom of the chart first) using the thread
 * path defined by the stitch, so peyote pairs up its offset columns and a
 * crochet rope is listed as rounds.
 */
export function wordChart(pattern: Pattern, opts: WordChartOptions = { useCodes: true, runLength: true }): string {
  const rows = wovenRows(pattern);
  const stitch = STITCH_SPECS[pattern.stitch];
  const size = physicalSize(pattern);
  const lines: string[] = [];

  const count = pattern.stitch === 'peyote' ? peyoteCount(pattern) : null;
  lines.push(pattern.name);
  lines.push(
    `${stitch.label}${count ? ` — ${count} count` : ''} · ` +
      `${pattern.width} × ${pattern.height} beads · ${BEAD_SPECS[pattern.beadSize].label}`,
  );
  lines.push(`Finished size approx ${size.w.toFixed(1)} × ${size.h.toFixed(1)} mm`);
  if (pattern.stitch === 'peyote') {
    lines.push('Rows 1 and 2 are strung together as one line of beads; row 3 starts the weaving.');
    if (count === 'odd') {
      lines.push(
        'Odd count: every second row ends with no bead to stitch into, so it needs the odd-count turnaround at that edge.',
      );
    }
  }
  if (pattern.stitch === 'crochet') {
    lines.push(`Rope: ${pattern.width} beads around, ${rows.length} rounds. String in the order below.`);
  }
  lines.push('');

  for (const row of rows) {
    const values = row.cells.map((c) => pattern.cells[c.row * pattern.width + c.col]);
    const parts: string[] = [];
    if (opts.runLength) {
      let i = 0;
      while (i < values.length) {
        let j = i;
        while (j < values.length && values[j] === values[i]) j += 1;
        const n = j - i;
        const label = values[i] === EMPTY ? 'skip' : labelOf(pattern.palette[values[i]], opts.useCodes);
        parts.push(n > 1 ? `${n}× ${label}` : label);
        i = j;
      }
    } else {
      for (const v of values) parts.push(v === EMPTY ? 'skip' : labelOf(pattern.palette[v], opts.useCodes));
    }
    const beadTotal = values.filter((v) => v !== EMPTY).length;
    lines.push(`${row.label} (${beadTotal} beads): ${parts.join(', ')}`);
  }

  return lines.join('\n');
}

export function shoppingList(pattern: Pattern): string {
  const rows = beadCounts(pattern);
  const spec = BEAD_SPECS[pattern.beadSize];
  const lines = [
    `${pattern.name} — bead list`,
    `${spec.label} · ${totalBeads(pattern)} beads total · approx ${threadEstimate(pattern).toFixed(1)} m of thread`,
    '',
    'Colour'.padEnd(30) + 'Code'.padEnd(10) + 'Beads'.padEnd(8) + '+spares'.padEnd(9) + 'grams',
  ];
  for (const r of rows) {
    lines.push(
      (r.color?.name ?? '').slice(0, 28).padEnd(30) +
        (r.color?.code ?? '').padEnd(10) +
        String(r.count).padEnd(8) +
        String(r.withSpares).padEnd(9) +
        r.grams.toFixed(2),
    );
  }
  lines.push('');
  lines.push('Spares allow ~12% for breakage and culling. Buy the next tube size up if you are close.');
  lines.push('Colour codes are approximate screen matches — check a physical colour card before ordering.');
  return lines.join('\n');
}
